import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
  type SyntheticEvent,
} from 'react';
import { SupportModal } from '../components/SupportModal';
import { RiftboundSignIn } from '../components/signin/RiftboundSignIn';
import {
  registerSupportModalOpener,
  shouldShowSupportModal,
} from '../utils/supportModalTracking';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ModalsContextValue {
  /**
   * Opens sign-in. A string replaces the dialog heading until it closes.
   * Also safe as `onClick={openSignIn}` — a click event is ignored.
   */
  openSignIn: (title?: string | SyntheticEvent) => void;
  closeSignIn: () => void;
  /** True while the sign-in dialog or mobile cover is open. */
  signInOpen: boolean;
  openSupport: () => void;
  closeSupport: () => void;
  /** Opens the support modal when engagement + cooldown rules pass. */
  tryOpenSupport: () => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const ModalsContext = createContext<ModalsContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

/** Single provider for all app-level modals. Mount once in App. */
export function ModalProvider({ children }: { children: ReactNode }) {
  const [signInOpen, setSignInOpen] = useState(false);
  const [signInTitle, setSignInTitle] = useState<string | null>(null);
  const [supportOpen, setSupportOpen] = useState(false);

  const openSignIn = useCallback((title?: string | SyntheticEvent) => {
    // Click handlers pass this function directly (`onClick={openSignIn}`), so the
    // first argument is often a mouse event. Only a real string is a heading.
    const heading = typeof title === 'string' ? title.trim() : '';
    setSignInTitle(heading || null);
    setSignInOpen(true);
  }, []);
  const closeSignIn = useCallback(() => {
    setSignInOpen(false);
    setSignInTitle(null);
  }, []);

  const openSupport = useCallback(() => setSupportOpen(true), []);
  const closeSupport = useCallback(() => setSupportOpen(false), []);

  const tryOpenSupport = useCallback(() => {
    if (shouldShowSupportModal()) {
      setSupportOpen(true);
    }
  }, []);

  useEffect(() => {
    registerSupportModalOpener(openSupport);
    return () => registerSupportModalOpener(null);
  }, [openSupport]);

  const value = useMemo<ModalsContextValue>(
    () => ({ openSignIn, closeSignIn, signInOpen, openSupport, closeSupport, tryOpenSupport }),
    [openSignIn, closeSignIn, signInOpen, openSupport, closeSupport, tryOpenSupport],
  );

  return (
    <ModalsContext.Provider value={value}>
      {children}
      <RiftboundSignIn open={signInOpen} title={signInTitle} onClose={closeSignIn} />
      <SupportModal open={supportOpen} onClose={closeSupport} />
    </ModalsContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useModals(): ModalsContextValue {
  const ctx = useContext(ModalsContext);
  if (!ctx) throw new Error('useModals must be used within ModalProvider');
  return ctx;
}
